/**
 * DealDetailsModal Component
 *
 * Displays individual deal details (stock number, gross) for auditing Forecast page numbers.
 * Fetches data on-demand when opened. Supports printing.
 *
 * Access: Admin, Executive, GeneralManager only (enforced by parent + API).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, PrinterIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { getDealDetails, DealDetail } from '../../services/api';

type SortKey = 'index' | 'stockNumber' | 'dealNumber' | 'customerLastName' | 'make' | 'saleDate' | 'status' | 'totalGross';
type SortDir = 'asc' | 'desc';

const STATUS_ORDER: Record<string, number> = { Processed: 0, Unprocessed: 1, Swap: 2, Wholesale: 3 };

interface DealDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
  year: number;
  month: number;
  department: 'NEW_VEHICLE' | 'USED_VEHICLE';
}

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DealDetailsModal: React.FC<DealDetailsModalProps> = ({
  isOpen,
  onClose,
  locationId,
  locationName,
  year,
  month,
  department,
}) => {
  const [deals, setDeals] = useState<DealDetail[]>([]);
  const [totalGross, setTotalGross] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('index');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const departmentLabel = department === 'NEW_VEHICLE' ? 'New Vehicle' : 'Used Vehicle';
  const monthName = MONTH_NAMES[month] || '';

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDealDetails(locationId, year, month, department);
      if (response.success && response.data) {
        setDeals(response.data.deals);
        setTotalGross(response.data.totalGross);
      } else {
        setError(response.error || 'Failed to load deal details');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load deal details');
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month, department]);

  useEffect(() => {
    if (isOpen) {
      fetchDeals();
      setSortKey('index');
      setSortDir('asc');
    } else {
      setDeals([]);
      setTotalGross(0);
      setError(null);
    }
  }, [isOpen, fetchDeals]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'totalGross' ? 'desc' : 'asc');
    }
  };

  const sortedDeals = useMemo(() => {
    if (sortKey === 'index') {
      return sortDir === 'asc' ? deals : [...deals].reverse();
    }
    return [...deals].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'stockNumber': cmp = a.stockNumber.localeCompare(b.stockNumber); break;
        case 'dealNumber': cmp = a.dealNumber.localeCompare(b.dealNumber); break;
        case 'customerLastName': cmp = (a.customerLastName || '').localeCompare(b.customerLastName || ''); break;
        case 'make': cmp = a.make.localeCompare(b.make); break;
        case 'saleDate': cmp = a.saleDate.localeCompare(b.saleDate); break;
        case 'status': cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9); break;
        case 'totalGross': cmp = a.totalGross - b.totalGross; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [deals, sortKey, sortDir]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  };

  const processedDeals = deals.filter(d => d.status === 'Processed');
  const unprocessedDeals = deals.filter(d => d.status === 'Unprocessed');
  const swapDeals = deals.filter(d => d.status === 'Swap');
  const wholesaleDeals = deals.filter(d => d.status === 'Wholesale');
  const soldDeals = deals.filter(d => d.status !== 'Swap' && d.status !== 'Wholesale');
  const processedGross = processedDeals.reduce((sum, d) => sum + d.totalGross, 0);
  const unprocessedGross = unprocessedDeals.reduce((sum, d) => sum + d.totalGross, 0);
  const swapGross = swapDeals.reduce((sum, d) => sum + d.totalGross, 0);
  const wholesaleGross = wholesaleDeals.reduce((sum, d) => sum + d.totalGross, 0);

  const handlePrint = () => {
    // Clone the modal content into a top-level wrapper so print CSS can target it
    // (the modal is nested inside backdrop divs that break print visibility)
    const source = document.getElementById('deal-details-print');
    if (!source) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'deal-details-print-wrapper';
    wrapper.innerHTML = source.innerHTML;
    document.body.appendChild(wrapper);
    document.body.classList.add('printing-deal-details');

    const cleanup = () => {
      document.body.classList.remove('printing-deal-details');
      wrapper.remove();
    };

    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 print-hidden" />

      {/* Modal */}
      <div id="deal-details-print" className="relative max-w-5xl w-full max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {departmentLabel} Deals
            </h2>
            <p className="text-sm text-gray-500">
              {locationName} — {monthName} {year}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print-hidden"
              title="Print"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print-hidden"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-500">Loading deals...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && deals.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No deals found for this period.
            </div>
          )}

          {!loading && !error && deals.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 text-left">
                  {([
                    ['index', '#', 'pr-3'],
                    ['stockNumber', 'Stock #', 'pr-3'],
                    ['dealNumber', 'Deal #', 'pr-3'],
                    ['customerLastName', 'Customer', 'pr-3'],
                    ['make', 'Make', 'pr-3'],
                    ['saleDate', 'Sale Date', 'pr-3'],
                    ['status', 'Status', 'pr-3'],
                    ['totalGross', 'Total Gross', 'text-right'],
                  ] as [SortKey, string, string][]).map(([key, label, cls]) => (
                    <th
                      key={key}
                      className={`py-2 ${cls} font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-600 transition-colors`}
                      onClick={() => handleSort(key)}
                    >
                      <span className="inline-flex items-center gap-0.5">
                        {label}
                        {sortKey === key && (
                          sortDir === 'asc'
                            ? <ChevronUpIcon className="h-3.5 w-3.5" />
                            : <ChevronDownIcon className="h-3.5 w-3.5" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDeals.map((deal, index) => (
                  <tr
                    key={`${deal.dealNumber}-${index}`}
                    className={`border-b border-gray-100 ${
                      deal.status === 'Unprocessed' ? 'bg-yellow-50' :
                      deal.status === 'Swap' ? 'bg-blue-50' :
                      deal.status === 'Wholesale' ? 'bg-purple-50' : ''
                    }`}
                  >
                    <td className="py-1.5 pr-3 text-gray-400">{index + 1}</td>
                    <td className="py-1.5 pr-3 font-mono text-gray-900">{deal.stockNumber}</td>
                    <td className="py-1.5 pr-3 font-mono text-gray-600">{deal.dealNumber}</td>
                    <td className="py-1.5 pr-3 text-gray-700">{deal.customerLastName}</td>
                    <td className="py-1.5 pr-3 text-gray-700">{deal.make}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{deal.saleDate}</td>
                    <td className="py-1.5 pr-3">
                      {deal.status === 'Processed' ? (
                        <span className="text-green-700 text-xs font-medium">Processed</span>
                      ) : deal.status === 'Swap' ? (
                        <span className="text-blue-700 text-xs font-medium">Swap</span>
                      ) : deal.status === 'Wholesale' ? (
                        <span className="text-purple-700 text-xs font-medium">Wholesale</span>
                      ) : (
                        <span className="text-yellow-700 text-xs font-medium">Unprocessed</span>
                      )}
                    </td>
                    <td className={`py-1.5 text-right font-mono ${deal.totalGross < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatCurrency(deal.totalGross)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer — Summary */}
        {!loading && !error && deals.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-4">
                <span className="text-gray-600">
                  <span className="font-semibold text-gray-900">{processedDeals.length}</span> Processed
                </span>
                {unprocessedDeals.length > 0 && (
                  <span className="text-gray-600">
                    <span className="font-semibold text-yellow-700">{unprocessedDeals.length}</span> Unprocessed
                  </span>
                )}
                {swapDeals.length > 0 && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-600">
                      <span className="font-semibold text-blue-700">{swapDeals.length}</span> Swaps
                    </span>
                  </>
                )}
                {wholesaleDeals.length > 0 && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-600">
                      <span className="font-semibold text-purple-700">{wholesaleDeals.length}</span> Wholesale
                    </span>
                  </>
                )}
                <span className="text-gray-600">
                  <span className="font-bold text-gray-900">{soldDeals.length}</span> Sold Units
                </span>
              </div>
              <div className="flex items-center gap-4">
                {swapDeals.length > 0 && (
                  <span className="text-gray-600">
                    Swaps: <span className="font-semibold text-blue-700">{formatCurrency(swapGross)}</span>
                  </span>
                )}
                {wholesaleDeals.length > 0 && (
                  <span className="text-gray-600">
                    Wholesale: <span className="font-semibold text-purple-700">{formatCurrency(wholesaleGross)}</span>
                  </span>
                )}
                <span className="text-gray-600">
                  Total: <span className="font-bold text-gray-900">{formatCurrency(totalGross)}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealDetailsModal;
